import React, { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';

import { useFetchData } from '../../hooks/useFetchData';
import { StorageKeys, useLocalStorageState } from '../../hooks/useLocalStorageState';
import { NotificationType } from '../../Types/enums';
import { Notification } from '../../Types/notifications';
import { notificationsPathUrl } from '../../util/routesUrls';
import { useAuth } from './AuthProvider';

const SOFT_POLL_INTERVAL = 20 * 60 * 1000; // 20 minutes

interface NotificationsContextType {
  notifications: Notification[];
  readNotifications: string[];
  unreadNotifications: Notification[];
  setReadNotifications: React.Dispatch<React.SetStateAction<string[]>>;
  refetchNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  readNotifications: [],
  unreadNotifications: [],
  setReadNotifications: () => {},
  refetchNotifications: () => Promise.resolve(),
});

export const useNotifications = () => useContext(NotificationsContext);

interface NotificationsProviderProps {
  children: ReactNode;
}

const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const { currentUser, refetchUser } = useAuth();
  const {
    data: notifications,
    refetch: refetchNotifications,
    startPolling,
    stopPolling,
  } = useFetchData<Notification[]>({
    urlPath: notificationsPathUrl(currentUser?.id || ''),
    skip: !currentUser,
  });
  const [readNotifications, setReadNotifications] = useLocalStorageState<string[]>(
    StorageKeys.READ_NOTIFICATIONS,
    []
  );

  const unreadNotifications = useMemo(
    () => notifications?.filter((n) => !readNotifications.includes(n.id)) || [],
    [notifications, readNotifications]
  );

  // Determine the shortest pollInterval from notifications or use the default
  const pollInterval = useMemo(() => {
    if (!notifications?.length) return SOFT_POLL_INTERVAL;

    const validIntervals = notifications
      .map((n) => n.pollInterval)
      .filter((p): p is number => p != null);

    return validIntervals.length ? Math.min(...validIntervals) : SOFT_POLL_INTERVAL;
  }, [notifications]);

  // Start polling every 20 minutes or the shortest pollInterval from notifications
  useEffect(() => {
    startPolling(pollInterval);

    return () => stopPolling();
  }, [pollInterval, startPolling, stopPolling]);

  useEffect(() => {
    const hasDeepResearchNotification = unreadNotifications.some(
      (n) => n.type === NotificationType.DEEP_RESEARCH_REFILL_APPROVED
    );

    if (hasDeepResearchNotification) {
      refetchUser(currentUser?.id || '');
    }
  }, [unreadNotifications, currentUser?.id, refetchUser]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications: notifications ?? [],
        readNotifications,
        unreadNotifications,
        setReadNotifications,
        refetchNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
